import React, { useState } from "react";
import ProgressBar from "./ProgressBar";
import PersonalInfo from "./PersonalInfo";
import FinancialInfo from "./FinancialInfo";
import SuperannuationInfo from "./SuperannuationInfo";
import ProfessionalInfo from "./ProfessionalInfo";
import UserSettings from "./UserSettings";
import { FiX } from "react-icons/fi";
import "../styles/AddMember.css";
import { graphqlRequest } from "../../../utils/graphqlRequest";
import { uploadToSignedUrl } from "../../../utils/uploadToSignedUrl";
import { toast } from "react-toastify";

const AddMember = ({ onClose, onMemberAdded, memberType }) => {
  const [currentStage, setCurrentStage] = useState(0);
  const [memberData, setMemberData] = useState({ memberType });
  const [submitting, setSubmitting] = useState(false);
  
  // Check if superannuation section should be shown (Admin, Manager, Cleaner only)
  const shouldShowSuperannuation = 
    memberType === "Admin" || 
    memberType === "Manager" || 
    memberType === "Cleaner";

  const handleNext = (data) => {
    setMemberData((prev) => ({ ...prev, ...data }));
    setCurrentStage((prev) => prev + 1);
  };

  const handlePrevious = () => {
    setCurrentStage((prev) => prev - 1);
  };

  // Map role from form to backend UserRole enum KEY (GraphQL uses enum keys, not values)
  const mapRoleToBackend = (role) => {
    const roleMap = {
      Admin: "ADMIN",
      Manager: "OPERATIONS_MANAGER",
      Cleaner: "FIELD_CLEANER",
      Contractor: "SCHEDULER_DISPATCHER",
      Staff: "FIELD_CLEANER", // Default to FIELD_CLEANER for Staff
    };
    return roleMap[role] || "FIELD_CLEANER";
  };

  // Map gender from form to backend enum (GraphQL enum format)
  const mapGenderToBackend = (gender) => {
    const genderMap = {
      Male: "MALE",
      Female: "FEMALE",
      Other: "OTHER",
    };
    return genderMap[gender] || null;
  };

  // Upload profile image to S3
  const uploadProfileImage = async (file) => {
    if (!file) return null;

    try {
      const generateUrlMutation = `
        mutation GenerateUrlForImageUpload($files: [Fileupload!]!) {
          generateUrlForImageUploadClient(files: $files) {
            key
            url
          }
        }
      `;

      const fileMetadata = [
        {
          fileName: file.name,
          contentType: file.type,
        },
      ];

      const data = await graphqlRequest(generateUrlMutation, {
        files: fileMetadata,
      });

      const { key, url } = data?.generateUrlForImageUploadClient?.[0] || {};

      if (!url || !key) {
        throw new Error("Failed to get upload URL");
      }

      return uploadToSignedUrl(url, key, file);
    } catch (error) {
      console.error("Error uploading profile image:", error);
      throw new Error(
        error?.message && error.message !== "Network Error"
          ? error.message
          : "Failed to upload profile photo. Try a smaller JPEG or PNG."
      );
    }
  };

  const handleSave = async (userSettingsData) => {
    // Combine all form data
    const allData = { ...memberData, ...userSettingsData };

    // Validation
    if (!allData.firstName || !allData.lastName) {
      toast.error(
        "First name and last name are required. Returning to Personal Info."
      );
      setCurrentStage(0);
      return;
    }

    if (!allData.email) {
      toast.error("Email is required");
      return;
    }

    if (!allData.password) {
      toast.error("Password is required");
      return;
    }

    if (allData.password !== allData.retypePassword) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      setSubmitting(true);

      // Upload profile image if provided
      let profileImageUrl = null;
      if (allData.profile) {
        profileImageUrl = await uploadProfileImage(allData.profile);
      }

      // Format DOB if provided
      let dobFormatted = null;
      if (allData.dob) {
        const date = new Date(allData.dob);
        dobFormatted = date.toISOString().split("T")[0];
      }

      // Build address string
      const addressParts = [
        allData.street,
        allData.cityState,
        allData.postalCode,
        allData.country,
      ].filter(Boolean);
      const address = addressParts.join(", ");

      // Build contact number
      const contact = allData.countryCode
        ? `${allData.countryCode}${allData.contactNumber}`
        : allData.contactNumber;

      // Create username from email if not provided
      const username =
        allData.username || allData.email.split("@")[0] || allData.email;

      // Determine role from memberType (which tab the form was opened from)
      const roleFromMemberType = memberType || allData.memberType || "Staff";
      const backendRole = mapRoleToBackend(roleFromMemberType);

      const mutation = `
        mutation RegisterUserByAdmin($input: RegisterUerByAdmin!) {
          registerUerByAdmin(RegisterUerByAdmin: $input) {
            id
            firstName
            lastName
            email
            roles
            upload
          }
        }
      `;

      // Format joining date if provided
      let joiningDateFormatted = null;
      if (allData.joiningDate) {
        const date = new Date(allData.joiningDate);
        joiningDateFormatted = date.toISOString().split("T")[0];
      }

      // Format visa expiry date if provided
      let visaExpiryDayFormatted = null;
      if (allData.visaExpiryDay) {
        const date = new Date(allData.visaExpiryDay);
        visaExpiryDayFormatted = date.toISOString().split("T")[0];
      }

      const variables = {
        input: {
          addedFromWorkforce: true,
          // Admin filled the full Add Member form — mark profile complete.
          profileComplete: true,
          firstName: allData.firstName,
          lastName: allData.lastName,
          ...(!shouldShowSuperannuation && allData.businessName && { businessName: allData.businessName }),
          ...(dobFormatted && { DOB: dobFormatted }),
          ...(contact && { contact }),
          ...(address && { address }),
          ...(allData.gender && { gender: mapGenderToBackend(allData.gender) }),
          password: allData.password,
          email: allData.email,
          username: username,
          roles: [backendRole],
          // Tax residency from Personal Info
          ...(allData.taxResidency && { taxResidency: allData.taxResidency }),
          // Financial Info
          ...(allData.bsb && { bsb: allData.bsb }),
          ...(allData.accountNumber && { accountNumber: allData.accountNumber }),
          ...(!shouldShowSuperannuation && allData.accountName && { accountName: allData.accountName }),
          ...(allData.taxFileNumber && { taxFileNumber: allData.taxFileNumber }),
          ...(!shouldShowSuperannuation && allData.abnNumber && { abnNumber: allData.abnNumber }),
          ...(allData.emergencyContactName && { emergencyContactName: allData.emergencyContactName }),
          ...(allData.emergencyContactNumber && { emergencyContactNumber: allData.emergencyContactNumber }),
          ...(allData.claimTaxFreeThreshold && { claimTaxFreeThreshold: allData.claimTaxFreeThreshold }),
          ...(allData.visaStatus && { visaStatus: allData.visaStatus }),
          ...(visaExpiryDayFormatted && { visaExpiryDay: visaExpiryDayFormatted }),
          ...(allData.otherVisaType && { otherVisaType: allData.otherVisaType }),
          // Superannuation Info (only for Admin, Manager, Cleaner)
          ...(shouldShowSuperannuation && allData.fundName && { fundName: allData.fundName }),
          ...(shouldShowSuperannuation && allData.spin && { spin: allData.spin }),
          ...(shouldShowSuperannuation && allData.fundABN && { fundABN: allData.fundABN }),
          ...(shouldShowSuperannuation && allData.fundTelephoneNumber && { fundTelephoneNumber: allData.fundTelephoneNumber }),
          ...(shouldShowSuperannuation && allData.memberNumber && { memberNumber: allData.memberNumber }),
          // Professional Info
          ...(allData.jobTitle && { jobTitle: allData.jobTitle }),
          ...(!shouldShowSuperannuation && allData.payType && { payType: allData.payType }),
          ...(allData.perHourRate && { perHourRate: parseFloat(allData.perHourRate) }),
          ...(allData.superAnnuation && { superAnnuation: parseFloat(allData.superAnnuation) }),
          ...(allData.salaryTerm && { salaryTerm: allData.salaryTerm }),
          ...(allData.typeOfWork && { typeOfWork: allData.typeOfWork }),
          ...(allData.paydayDay && { paydayDay: allData.paydayDay }),
          ...(allData.paydayTime && { paydayTime: allData.paydayTime }),
          ...(joiningDateFormatted && { joiningDate: joiningDateFormatted }),
          ...(allData.bonus && { bonus: parseFloat(allData.bonus) }),
          ...(allData.allowance && { allowance: parseFloat(allData.allowance) }),
          ...(allData.commission && { commission: parseFloat(allData.commission) }),
          ...(profileImageUrl && { upload: profileImageUrl }),
        },
      };

      const result = await graphqlRequest(mutation, variables);
      const createdUser = result?.registerUerByAdmin;

      // Update profile image if uploaded
      if (profileImageUrl && createdUser?.id) {
        try {
          const updateMutation = `
            mutation UpdateUserByAdmin($id: String!, $input: UpdateUserByAdmin!) {
              updateUserByAdmin(id: $id, UpdateUserByAdmin: $input) {
                id
                upload
              }
            }
          `;

          await graphqlRequest(updateMutation, {
            id: createdUser.id,
            input: {
              upload: profileImageUrl,
            },
          });
        } catch (err) {
          console.error("Error updating profile image:", err);
          // Don't fail the whole operation if image update fails
        }
      }

      // Trigger refresh callback - this will close the modal and refresh data
      toast.success("Employee profile saved in Workforce.");
      window.setTimeout(() => {
        window.dispatchEvent(new Event('admin-backend-notifications-refresh'));
      }, 400);
      if (onMemberAdded) {
        onMemberAdded();
      } else {
        onClose();
      }
    } catch (err) {
      console.error("Error creating member:", err);
      const errorMessage =
        err?.response?.data?.errors?.[0]?.message ||
        err?.message ||
        "Failed to create member";
      if (/was created/i.test(errorMessage)) {
        toast.warn(errorMessage);
        if (onMemberAdded) onMemberAdded();
        else onClose();
        return;
      }
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStage = () => {
    // Define stages based on whether superannuation should be shown
    const stages = shouldShowSuperannuation
      ? ['personal', 'financial', 'superannuation', 'professional', 'settings']
      : ['personal', 'financial', 'professional', 'settings'];
    
    const currentStageName = stages[currentStage];
    
    switch (currentStageName) {
      case 'personal':
        return (
          <PersonalInfo
            onNext={handleNext}
            initialData={memberData}
          />
        );
      case 'financial':
        return (
          <FinancialInfo
            onNext={handleNext}
            onPrevious={handlePrevious}
            initialData={memberData}
          />
        );
      case 'superannuation':
        return (
          <SuperannuationInfo
            onNext={handleNext}
            onPrevious={handlePrevious}
            initialData={memberData}
          />
        );
      case 'professional':
        return (
          <ProfessionalInfo
            onNext={handleNext}
            onPrevious={handlePrevious}
            initialData={memberData}
          />
        );
      case 'settings':
        return (
          <UserSettings
            onPrevious={handlePrevious}
            onSave={handleSave}
            initialData={memberData}
            submitting={submitting}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="add-member-overlay">
      <div className="add-member-container">
        <div className="add-member-header">
          <h2>Add {memberType || "Member"}</h2>
          <button className="close-btn" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        <ProgressBar currentStage={currentStage} shouldShowSuperannuation={shouldShowSuperannuation} />

        <div className="stage-container">{renderStage()}</div>
      </div>
    </div>
  );
};

export default AddMember;
